import { isTourFlightSelectionStepAvailable } from './tourFlightSelectors'
import { createSelector } from 'reselect'
import { CheckoutPageId } from 'checkout/constants/pages'
import { selectIsGuestCheckoutMode } from 'selectors/checkoutSelectors'
import getExperienceItems from './view/items/getExperienceItems'
import getAccommodationV2Items from './view/items/getAccommodationV2Items'
import { getLeInstantBookingItems } from './view/accommodation'
import { getBedbankItems } from './view/hotels'
import { getVillaItems } from './view/villa'
import {
  isExperiencesSupportedToAddToCart,
  isAirportTransfersStepAvailable,
  isAddFlightsStepRouteAvailable,
} from './navigationSelectors'
import { isLuxPlusStandaloneStepAvailableWithContent } from './luxPlusStepAvailability'

/**
 * Central mapping of checkout step IDs to their availability selectors.
 *
 * When adding a new funnel-gated step:
 * 1. Create the availability selector in navigationSelectors.ts
 * 2. Add it to this mapping
 *
 * LuxPlus also has a content gate in luxPlusStepAvailability.ts
 * (`isLuxPlusStandaloneStepAvailableWithContent`) so the step is skipped when
 * funnel eligibility is true but the cart has no eligible upsell.
 *
 * The guard in CheckoutPage.tsx will automatically handle skip + no-flash.
 */
const hasHotelExperiencesContext = createSelector(
  (state: App.State) => getLeInstantBookingItems(state),
  (state: App.State) => getBedbankItems(state),
  (state: App.State) => getVillaItems(state),
  (state: App.State) => getAccommodationV2Items(state),
  (instantBookingHotelItems, bedbankItems, villaItems, accommodationV2Items): boolean =>
    instantBookingHotelItems.length > 0 || bedbankItems.length > 0 || villaItems.length > 0 || accommodationV2Items.length > 0,
)

const isExperiencesStepAvailable = createSelector(
  (state: App.State) => isExperiencesSupportedToAddToCart(state),
  (state: App.State) => hasHotelExperiencesContext(state),
  (state: App.State) => getExperienceItems(state),
  (isSupportedToAddToCart, hasHotelContext, experienceItems): boolean =>
    isSupportedToAddToCart || (hasHotelContext && experienceItems.length > 0),
)

const stepAvailabilitySelectors: Partial<Record<CheckoutPageId, (state: App.State) => boolean>> = {
  [CheckoutPageId.AirportTransfers]: isAirportTransfersStepAvailable,
  [CheckoutPageId.Experiences]: isExperiencesStepAvailable,
  [CheckoutPageId.LuxPlus]: isLuxPlusStandaloneStepAvailableWithContent,
  [CheckoutPageId.AddFlights]: isAddFlightsStepRouteAvailable,
  [CheckoutPageId.DepartingFlights]: isTourFlightSelectionStepAvailable,
  [CheckoutPageId.ReturningFlights]: isTourFlightSelectionStepAvailable,
}

export const getIsStepAvailable = (stepId: string) =>
  createSelector(
    (state: App.State) => state,
    (state): boolean => {
      if (selectIsGuestCheckoutMode(state)) {
        return stepId === CheckoutPageId.Purchase ||
          (stepId === CheckoutPageId.LuxPlus && isLuxPlusStandaloneStepAvailableWithContent(state))
      }

      const selector = stepAvailabilitySelectors[stepId as CheckoutPageId]
      return selector ? selector(state) : true
    },
  )

export const isStepGated = (stepId: string): boolean =>
  (stepId as CheckoutPageId) in stepAvailabilitySelectors
